import {
  ArrayMaxSize,
  IsArray,
  IsBoolean,
  IsEmail,
  IsIn,
  IsISO8601,
  IsOptional,
  IsString,
  IsUUID,
  MaxLength,
  MinLength,
} from 'class-validator';

/**
 * Front desk — the call and walk-in log.
 *
 * A clerk logs a phone call, a walk-in or an email in seconds: who (or
 * anonymous), how to reach them, the property, the topic, notes, the files it
 * was about and what came of it. An entry can be turned into a new file through
 * the normal intake path, attached to an existing file's history, handed to the
 * public-report triage queue, or given a callback task with an assignee and a
 * due time.
 *
 * Shared by the Government Portal web app, the mobile app and gov-api.
 */

export const CONTACT_CHANNELS = ['phone', 'walk_in', 'email'] as const;
export type ContactChannel = (typeof CONTACT_CHANNELS)[number];

export const CONTACT_TOPICS = [
  'new_complaint',
  'file_follow_up',
  'permit',
  'ticket_payment',
  'general_inquiry',
  'other',
] as const;
export type ContactTopic = (typeof CONTACT_TOPICS)[number];

export const CONTACT_OUTCOMES = [
  'resolved',
  'callback',
  'referred',
  'file_opened',
  'attached',
  'sent_to_triage',
] as const;
export type ContactOutcome = (typeof CONTACT_OUTCOMES)[number];

/** Outcomes a clerk picks on the form; the others are set by an action. */
export const PICKABLE_CONTACT_OUTCOMES = ['resolved', 'callback', 'referred'] as const;
export type PickableContactOutcome = (typeof PICKABLE_CONTACT_OUTCOMES)[number];

/** The intake method a file opened from a log entry is recorded under. */
export const CONTACT_CHANNEL_INTAKE_METHOD: Record<ContactChannel, string> = {
  phone: 'phone_call',
  walk_in: 'in_person',
  email: 'email',
};

export const CONTACT_NOTES_MAX = 4000;
export const CONTACT_LINKED_FILES_MAX = 10;

/** A file a log entry is linked to, as the reader may see it. */
export interface ContactLinkedFile {
  id: string;
  reference: string;
  address: string;
  /** True when the reader sees this file only as the public would. */
  redacted: boolean;
}

export interface ContactCallbackView {
  assigneeId: string;
  assigneeName: string;
  dueAt: string;
  doneAt: string | null;
  doneNote: string | null;
  /** Due time passed and not done. Computed server-side at read time. */
  overdue: boolean;
}

export interface ContactLogEntryView {
  id: string;
  localGovernmentId: string;
  localGovernmentName: string;
  channel: ContactChannel;
  anonymous: boolean;
  /**
   * Null when anonymous and the reader is not front-desk staff, or when the
   * entry is shown on a file to a reader who may not see who called.
   */
  callerName: string | null;
  callerPhone: string | null;
  callerEmail: string | null;
  address: string | null;
  pid: string | null;
  ownerName: string | null;
  topic: ContactTopic | string;
  notes: string | null;
  outcome: ContactOutcome;
  files: ContactLinkedFile[];
  openedFileId: string | null;
  triageComplaintId: string | null;
  callback: ContactCallbackView | null;
  createdById: string | null;
  createdByName: string;
  createdAt: string;
  /** The reader's own entry, or they hold front desk at its municipality. */
  canEdit: boolean;
  /** Caller identity and notes withheld from this reader. */
  redacted: boolean;
}

/** `GET /front-desk/status` — where the caller may keep the log. */
export interface FrontDeskStatus {
  enabled: boolean;
  localGovernments: { id: string; name: string }[];
}

/** `GET /front-desk/entries`. */
export interface ContactLogList {
  items: ContactLogEntryView[];
}

/** `GET /front-desk/callbacks/mine`. */
export interface MyCallbacks {
  open: ContactLogEntryView[];
  overdueCount: number;
  /** Done in the last seven days, newest first. */
  recentlyDone: ContactLogEntryView[];
}

/** `GET /files/:fileId/contact-history`. */
export interface FileContactHistory {
  enabled: boolean;
  /** Linked to this file. */
  linked: ContactLogEntryView[];
  /** About the same property (address or PID) or owner, not linked. */
  sameProperty: ContactLogEntryView[];
}

/** `GET /front-desk/file-lookup?q=`. */
export interface ContactFileLookup {
  results: ContactLinkedFile[];
}

/** `GET /front-desk/assignees?localGovernmentId=`. */
export interface ContactAssignee {
  id: string;
  displayName: string;
}

/** `POST /front-desk/entries`. */
export class CreateContactLogDto {
  @IsUUID()
  localGovernmentId!: string;

  @IsIn(CONTACT_CHANNELS)
  channel!: ContactChannel;

  @IsOptional()
  @IsBoolean()
  anonymous?: boolean;

  @IsOptional()
  @IsString()
  @MaxLength(200)
  callerName?: string;

  @IsOptional()
  @IsString()
  @MaxLength(32)
  callerPhone?: string;

  @IsOptional()
  @IsEmail()
  @MaxLength(320)
  callerEmail?: string;

  @IsOptional()
  @IsString()
  @MaxLength(400)
  address?: string;

  @IsOptional()
  @IsString()
  @MaxLength(32)
  pid?: string;

  @IsOptional()
  @IsString()
  @MaxLength(200)
  ownerName?: string;

  @IsIn(CONTACT_TOPICS)
  topic!: ContactTopic;

  @IsOptional()
  @IsString()
  @MaxLength(CONTACT_NOTES_MAX)
  notes?: string;

  @IsOptional()
  @IsIn(PICKABLE_CONTACT_OUTCOMES)
  outcome?: PickableContactOutcome;

  @IsOptional()
  @IsArray()
  @ArrayMaxSize(CONTACT_LINKED_FILES_MAX)
  @IsUUID('4', { each: true })
  fileIds?: string[];

  /** Callback: both or neither. */
  @IsOptional()
  @IsUUID()
  callbackAssigneeId?: string;

  @IsOptional()
  @IsISO8601()
  callbackDueAt?: string;
}

/** `PATCH /front-desk/entries/:id` — every field optional. */
export class UpdateContactLogDto {
  @IsOptional()
  @IsIn(CONTACT_CHANNELS)
  channel?: ContactChannel;

  @IsOptional()
  @IsBoolean()
  anonymous?: boolean;

  @IsOptional()
  @IsString()
  @MaxLength(200)
  callerName?: string;

  @IsOptional()
  @IsString()
  @MaxLength(32)
  callerPhone?: string;

  @IsOptional()
  @IsString()
  @MaxLength(320)
  callerEmail?: string;

  @IsOptional()
  @IsString()
  @MaxLength(400)
  address?: string;

  @IsOptional()
  @IsString()
  @MaxLength(32)
  pid?: string;

  @IsOptional()
  @IsString()
  @MaxLength(200)
  ownerName?: string;

  @IsOptional()
  @IsIn(CONTACT_TOPICS)
  topic?: ContactTopic;

  @IsOptional()
  @IsString()
  @MaxLength(CONTACT_NOTES_MAX)
  notes?: string;

  @IsOptional()
  @IsIn(PICKABLE_CONTACT_OUTCOMES)
  outcome?: PickableContactOutcome;
}

/** `POST /front-desk/entries/:id/files` — attach to an existing file's history. */
export class AttachContactLogDto {
  @IsUUID()
  fileId!: string;
}

/**
 * `POST /front-desk/entries/:id/opened-file` — record the file intake opened
 * from this entry (the web/mobile client opens it through the normal
 * `POST /files` path, prefilled, then calls this).
 */
export class RecordOpenedFileDto {
  @IsUUID()
  fileId!: string;
}

/** `PUT /front-desk/entries/:id/callback` — set or reassign the callback. */
export class SetCallbackDto {
  @IsUUID()
  assigneeId!: string;

  @IsISO8601()
  dueAt!: string;
}

/** `POST /front-desk/entries/:id/callback/done`. */
export class CompleteCallbackDto {
  @IsOptional()
  @IsString()
  @MaxLength(1000)
  note?: string;
}

/** `POST /front-desk/entries/:id/triage` — hand to the public-report queue. */
export class SendContactToTriageDto {
  /** The description the triage queue shows; defaults to the notes. */
  @IsOptional()
  @IsString()
  @MinLength(1)
  @MaxLength(CONTACT_NOTES_MAX)
  description?: string;
}

/** Whether a callback is overdue at `now`. Shared by API and clients. */
export function isCallbackOverdue(
  callback: { dueAt: string; doneAt: string | null } | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!callback || callback.doneAt) return false;
  return new Date(callback.dueAt).getTime() < now.getTime();
}

/**
 * Query string for opening the intake form prefilled from a log entry. The
 * intake screen reads `contactLogId` and fetches the entry itself, so a reload
 * keeps the link rather than silently opening an unlinked file.
 */
export function intakeHrefForContact(entryId: string): string {
  return `/files/new?contactLogId=${encodeURIComponent(entryId)}`;
}
